import Link from '@zenbusiness/zui/Link';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_COMMON_PROPS } from './constants';

const ListItem = superstyled({
  name: 'ListItem',
  slot: 'ListItem',
  tag: 'li',
  styles: ({ ordered }) => ({
    listStyle: ordered ? 'decimal' : 'disc',
    margin: '0.5',
    ':has(> ol)': {
      listStyleType: 'decimal'
    }
  })
});

const OrderedListItem = superstyled({
  name: 'OrderedListItem',
  slot: 'OrderedListItem',
  tag: 'ol',
  styles: ({ theme, viewType }) => ({
    marginBlockStart: 0,
    paddingBlockStart: 0,
    paddingBlockEnd: 0,
    paddingLeft: theme.spacing(3), // remove default browser top-margin
    // Had to add negative border as I was not able to control the extra space above and below the UL and OL.
    '& > li': {
      listStyleType: 'disc',
      // '&:first-of-type': {
      marginBlockStart: viewType === 'report' ? '0rem' : '-0.5rem'
      // }
    },
    '& > li:last-of-type': {
      marginBlockEnd: viewType === 'report' ? '0rem' : '-1.5rem'
    },
    '& > li:first-of-type': {
      marginBlockStart: viewType === 'report' ? '0rem' : '-1.5rem'
    }
  })
});

const UnorderedListItem = superstyled({
  name: 'UnorderedListItem',
  slot: 'UnorderedListItem',
  tag: 'ul',
  styles: ({ theme, viewType }) => ({
    marginBlockStart: 0,
    paddingBlockStart: 0,
    paddingBlockEnd: 0,
    // marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& > li': {
      listStyleType: 'disc',
      // '&:first-of-type': {
      marginBlockStart: viewType === 'report' ? '0rem' : '-0.35rem'
      // }
    },
    '& > li:last-of-type': {
      marginBlockEnd: viewType === 'report' ? '0rem' : '-1rem'
    },
    '& > li:first-of-type': {
      marginBlockStart: viewType === 'report' ? '0rem' : '-1rem'
    }
  })
});

const Header = superstyled({
  name: 'Header',
  slot: 'Header',
  tag: 'h3',
  styles: ({ theme }) => ({
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2)
  })
});

const StyledLink = superstyled({
  name: 'StyledLink',
  slot: 'StyledLink',
  tag: Link,
  styles: () => ({
    lineHeight: '1.5'
  })
});

// Define components outside render to avoid recreation on every render
export const markdownComponents = {
  h1: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
  h2: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
  h3: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
  a: ({ href, children }) => {
    const clickstreamProps = {
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'anchor_tag',
      elementText: children,
      destination: href
    };
    return (
      <Link clickstreamProps={clickstreamProps} href={href} target="_blank">
        {children}
      </Link>
    );
  },
  p: ({ node, ...props }) => <Text size="md" {...props} />,
  strong: ({ node, ...props }) => <Text component="span" fontWeight="bold" {...props} />,
  em: ({ node, ...props }) => <Text component="span" fontStyle="italic" {...props} />,
  del: ({ node, ...props }) => <Text textDecoration="line-through" {...props} marginBottom="0" />,
  ul: ({ node, ...props }) => <UnorderedListItem as="ul" {...props} />,
  ol: ({ node, ...props }) => <OrderedListItem as="ol" {...props} />,
  li: ({ node, ordered, ...props }) => <ListItem {...props} margin="0.5" ordered={ordered} />
};

// Doesnt work for anything but simple p tags
// export const markdownComponentsWithTyping = {
//   h1: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
//   h2: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
//   h3: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
//   a: ({ href, children }) => <Link href={href}>{children}</Link>,
//   p: ({ children, ...props }) => {
//     const fullText = Children.toArray(children).join('');
//     return <TypingEffect as="p" text={fullText} {...props} />;
//   },
//   strong: ({ node, ...props }) => <Text component="span" fontWeight="bold" {...props} />,
//   em: ({ node, ...props }) => <Text component="span" fontStyle="italic" {...props} />,
//   del: ({ node, ...props }) => <Text textDecoration="line-through" {...props} marginBottom="0" />,
//   ul: ({ node, ...props }) => <UnorderedListItem as="ul" {...props} />,
//   ol: ({ node, ...props }) => <OrderedListItem as="ol" {...props} />,
//   li: ({ node, ordered, ...props }) => <ListItem {...props} margin="0.5" ordered={ordered} />
// };

export const articleMarkdownComponents = {
  h3: ({ node, ...props }) => <Header fontSize="lg" fontWeight="bold" {...props} />,
  a: ({ href, children }) => (
    <StyledLink href={href} target="_blank">
      {children}
    </StyledLink>
  ),
  p: ({ node, ...props }) => <Text size="sm" {...props} />,
  strong: ({ node, ...props }) => <Text component="span" fontWeight="bold" size="sm" {...props} />,
  em: ({ node, ...props }) => <Text component="span" fontStyle="italic" size="sm" {...props} />,
  del: ({ node, ...props }) => (
    <Text size="sm" textDecoration="line-through" {...props} marginBottom="0" />
  ),
  ul: ({ node, ...props }) => <UnorderedListItem as="ul" size="sm" {...props} />,
  ol: ({ node, ...props }) => <OrderedListItem as="ol" size="sm" {...props} />,
  li: ({ node, ordered, ...props }) => <ListItem {...props} margin="0.5" ordered={ordered} />
};

export const reportMarkdownComponents = {
  h1: ({ node, ...props }) => <Header fontSize="lg" weight="medium" {...props} />,
  h2: ({ node, ...props }) => <Header fontSize="lg" weight="medium" {...props} />,
  h3: ({ node, ...props }) => <Header fontSize="lg" weight="medium" {...props} />,
  a: ({ href, children }) => {
    const clickstreamProps = {
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'anchor_tag',
      elementText: children,
      destination: href
    };
    return (
      <Link clickstreamProps={clickstreamProps} href={href} target="_blank">
        {children}
      </Link>
    );
  },
  p: ({ node, ...props }) => <Text size="md" {...props} />,
  strong: ({ node, ...props }) => <Text component="span" weight="medium" {...props} />,
  em: ({ node, ...props }) => <Text component="span" fontStyle="italic" {...props} />,
  del: ({ node, ...props }) => <Text textDecoration="line-through" {...props} marginBottom="0" />,
  ul: ({ node, ...props }) => <UnorderedListItem as="ul" {...props} viewType="report" />,
  ol: ({ node, ...props }) => <OrderedListItem as="ol" {...props} viewType="report" />,
  li: ({ node, ordered, ...props }) => <ListItem {...props} margin="0.5" ordered={ordered} />
};
